import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import type { SimilarRecords } from '@vwt/contracts';
import { Badge, Button, cn } from '@vwt/ui';
import { api, useApiMutation } from '../lib/api.js';
import { toastError } from '../lib/errors.js';
import { useTranslation } from '../lib/i18n.js';
import { KeyLabel } from './badges.js';
import { useDebounced } from '../lib/debounce.js';

/**
 * Possible duplicates while writing a new work item (ADR-0025): text matches appear as the title is
 * typed; "Check with AI" asks the model which ones mean the same thing. Suggestions only; links open
 * in a new tab so the form is kept.
 */
export function SimilarWork({ kind, title, description, excludeId, className }: { kind: 'WORK' | 'REQUEST'; title: string; description?: string; excludeId?: string; className?: string }) {
  const { t } = useTranslation();
  const query = useDebounced(title.trim(), 500);
  const [found, setFound] = useState<SimilarRecords | null>(null);
  const ai = useApiMutation('findSimilar');
  useEffect(() => {
    setFound(null);
    if (query.length < 8) return;
    const controller = new AbortController();
    api
      .call('findSimilar', { body: { kind, title: query, ...(excludeId ? { excludeId } : {}) }, signal: controller.signal })
      .then((result) => setFound(result.data))
      .catch(() => undefined);
    return () => controller.abort();
  }, [query, kind, excludeId]);
  const check = async () => {
    try {
      const result = await ai.mutateAsync({ body: { kind, title: title.trim(), ...(description?.trim() ? { descriptionMarkdown: description } : {}), ...(excludeId ? { excludeId } : {}), ai: true } });
      setFound(result.data);
    } catch (error) {
      toastError(error);
    }
  };
  if (!found || (found.generatedBy === 'search' && !found.candidates.length)) return null;
  const judged = found.generatedBy !== 'search';
  return (
    <div className={cn('rounded-[var(--radius)] border border-warning/40 bg-warning-soft/40 px-3 py-2 text-sm', className)} data-testid="similar-work">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{judged ? (found.candidates.length ? t('similar.aiTitle') : t('similar.aiNone')) : t('similar.title')}</span>
        {judged ? <Badge tone="outline">{found.generatedBy === 'fixture' ? t('similar.fixture') : t('similar.byAi')}</Badge> : null}
        {!judged ? (
          <Button type="button" size="sm" variant="ghost" className="ml-auto" loading={ai.isPending} onClick={() => void check()}>
            <Sparkles /> {t('similar.check')}
          </Button>
        ) : null}
      </div>
      {found.candidates.length ? (
        <ul className="mt-1.5 flex flex-col gap-1">
          {found.candidates.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2">
              <KeyLabel value={c.key} />
              <a href={kind === 'WORK' ? `/work/${c.key}` : `/requests/${c.key}`} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate hover:underline">
                {c.title}
              </a>
              <span className="text-xs text-fg-subtle">{kind === 'WORK' ? t(`status.phase.${c.status}`) : t(`status.request.${c.status}`)}</span>
              {c.confidence !== 'LOW' ? <Badge tone={c.confidence === 'HIGH' ? 'warning' : 'outline'}>{t(`similar.confidence.${c.confidence}`)}</Badge> : null}
              {c.reason ? <span className="w-full pl-1 text-xs text-fg-muted">{c.reason}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
